"use client";

import type { JSX } from "react";
import HardwareCard from "@/components/HardwareCard";
import Section from "@/components/Section";
import Link from "next/link";
import { Check } from "lucide-react";
import ProductPage from "@/components/ProductPage";
import ImageHero from "./preise.jpg";

/* =========================
   TYPES
========================= */

type HardwareItem = {
  imageSrc: string;
  imageAlt: string;
  title: string;
  subtitle: string;
  description: string;
};

type HardwareCategory = {
  label: string;
  items: HardwareItem[];
};

type PricingCardProps = {
  title: string;
  priceFull: string;
  priceCents: string;
  details: JSX.Element | string;
  details2?: string;
  features: string[];
  periodLabel?: string;
};

/* =========================
   HELPERS
========================= */

const buildSupportUrl = (produkt: string, preis: string) => {
  const subject = `Anfrage: ${produkt}`;
  const body = `Guten Tag,\n\nich interessiere mich für das Angebot "${produkt}" zum Preis von ${preis}.\n\nBitte nehmen Sie Kontakt mit mir auf.`;
  return `/support?produkt=${encodeURIComponent(produkt)}&preis=${encodeURIComponent(preis)}&subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}#formular`;
};

const formatPrice = (full: string, cents: string, periodLabel: string) =>
  `${full},${cents} € / ${periodLabel}`;

/* =========================
   COMPONENTS
========================= */

const PricingCard = ({
  title,
  priceFull,
  priceCents,
  details,
  details2,
  features,
  periodLabel = "monatlich",
}: PricingCardProps) => {
  const priceString = formatPrice(priceFull, priceCents, periodLabel);
  const supportUrl = buildSupportUrl(title, priceString);

  return (
    <Link href={supportUrl} className="group block">
      <div className="bg-background rounded-sm px-7 py-5 hover:shadow-lg ease-in-out duration-300 flex flex-col min-h-105 border border-slate-100">
        <h2 className="text-4xl mb-6">{title}</h2>

        <div className="flex flex-row">
          <p className="font-body md:tracking-tighter m-0 text-[90px] leading-22.5">
            {priceFull}
          </p>
          <div className="font-body flex flex-col ml-4">
            <p className="m-0 text-2xl leading-14.25 sm:text-[32px] grow">
              ,{priceCents}
            </p>
            <p className="m-0 font-bold leading-9 text-md sm:text-xl">
              € {periodLabel}
            </p>
          </div>
        </div>

        <p className="text-grey text-sm mt-1">{details}</p>
        {details2 && <p className="text-grey text-sm mt-1">{details2}</p>}

        <div className="mt-4 richText strong-link text-grey text-left sm:max-w-none max-w-xs w-full sm:w-auto">
          <ul className="space-y-1">
            {features.map((feature, index) => (
              <li key={index} className="flex items-center gap-2">
                <Check className="text-brand-primary shrink-0" size={18} />
                {feature}
              </li>
            ))}
          </ul>
        </div>

        <span className="mt-12 pt-5 inline-flex items-center justify-center bg-brand-primary text-white py-5 px-10 rounded-full font-bold uppercase text-xs tracking-widest transition-all hover:bg-brand-primary-dark hover:-translate-y-1 hover:shadow-xl">
          Unverbindlich anfragen
        </span>
      </div>
    </Link>
  );
};

/* =========================
   DATA
========================= */

const hardwareCategories: HardwareCategory[] = [
  {
    label: "",
    items: [
      {
        imageSrc: "/hardware/UCC.jpg",
        imageAlt: "Kassenschublade UCC",
        title: "Kassenschublade UCC",
        subtitle: "200,00 €",
        description:
          "Die Geldkassette Universal Cash Cassette (UCC) verfügt über Standardmaße für den Einsatz mit den gängigen Kassensystemen.",
      },
      {
        imageSrc: "/hardware/E3959.jpg",
        imageAlt: "Kassenschublade E3959",
        title: "Kassenschublade E3959",
        subtitle: "200,00 €",
        description:
          "Robuste Kassenschublade für Deinen Tresen – zuverlässig und langlebig.",
      },
      {
        imageSrc: "/hardware/APGNANO.jpg",
        imageAlt: "Kassenschublade APGNANO",
        title: "Kassenschublade APGNANO",
        subtitle: "200,00 €",
        description:
          "Kompakte Nano-Kassenschublade, ideal für platzsparende Setups.",
      },
      {
        imageSrc: "/hardware/ARTDEVAC.jpg",
        imageAlt: "Kassenschublade ARTDEVAC",
        title: "Kassenschublade ARTDEVAC",
        subtitle: "200,00 €",
        description:
          "Stabile Kassenschublade mit zuverlässigem Schloss und großem Fassungsvermögen.",
      },
    ],
  },
  {
    label: "",
    items: [
      {
        imageSrc: "/hardware/ARTDEVTouch.jpg",
        imageAlt: "Kassenbildschirm ARTDEVTouch",
        title: "ARTDEVTouch",
        subtitle: "1.389,00 €",
        description:
          "Professioneller Touch-Kassenbildschirm für anspruchsvolle Kassensysteme.",
      },
      {
        imageSrc: "/hardware/iiyamaProLight.jpg",
        imageAlt: "Kassenbildschirm iiyama ProLight",
        title: "iiyama ProLight",
        subtitle: "989,00 €",
        description:
          "Hochwertiger iiyama ProLight Touchscreen – hell, scharf und zuverlässig.",
      },
      {
        imageSrc: "/hardware/kasseTouch-1.webp",
        imageAlt: "Kassenbildschirm KasseTouch 1",
        title: "POS KasseTouch V1",
        subtitle: "2.289,00 €",
        description:
          "Premium Touchscreen-Kassensystem für hohe Anforderungen im Tagesgeschäft.",
      },
      {
        imageSrc: "/hardware/kasseTouch-2.webp",
        imageAlt: "Kassenbildschirm KasseTouch 2",
        title: "POS KasseTouch V2",
        subtitle: "2.289,00 €",
        description:
          "Leistungsstarkes Touchscreen-Kassensystem mit erweiterten Funktionen.",
      },
    ],
  },
  {
    label: "",
    items: [
      {
        imageSrc: "/hardware/D3.jpg",
        imageAlt: "Kundendisplay D3",
        title: "Kundendisplay D3",
        subtitle: "149,00 €",
        description:
          "Die D3 80MM von Sunmi ist die Einstiegvariante in ein All-In-One Android Kassensystem mit 15.6 Display",
      },
      {
        imageSrc: "/hardware/DMD30.jpg",
        imageAlt: "Kundendisplay DMD30",
        title: "Kundendisplay DMD30",
        subtitle: "149,00 €",
        description:
          "Kompaktes Kundendisplay für transparente Preisanzeige am Point-of-Sale.",
      },
      {
        imageSrc: "/hardware/EpsonDMD30.jpg",
        imageAlt: "Kundendisplay Epson DMD30",
        title: "Epson DMD30",
        subtitle: "149,00 €",
        description:
          "Epson Kundendisplay – zuverlässig und gut lesbar für Deine Kunden.",
      },
      {
        imageSrc: "/hardware/LCD750.jpg",
        imageAlt: "Kundendisplay LCD750",
        title: "Kundendisplay LCD750",
        subtitle: "Auf Anfrage",
        description: "Hochwertiges LCD-Kundendisplay mit klarer Darstellung.",
      },
    ],
  },
  {
    label: "",
    items: [
      {
        imageSrc: "/hardware/AS-7210.jpg",
        imageAlt:
          "Bluetooth/Batch-Laser-Barcodescanner mit Display, USB-Anschluss",
        title: "AS-7210",
        subtitle: "149,00 €",
        description:
          "Die neueste Edition der AS-7210 Serie von mobilen Barcodescannern und Barcodesammlern für die Inventur.",
      },
      {
        imageSrc: "/hardware/AS-7210.jpg",
        imageAlt:
          "Bluetooth/Batch-Laser-Barcodescanner mit Display, USB-Anschluss",
        title: "AS-7310",
        subtitle: "149,00 €",
        description:
          "Die neueste Edition der AS-7310 Serie von mobilen Barcodescannern und Barcodesammlern für die Inventur.",
      },
      {
        imageSrc: "/hardware/DatalogicPowerScan.jpg",
        imageAlt: "Scanner Datalogic PowerScan",
        title: "Datalogic PowerScan",
        subtitle: "149,00 €",
        description:
          "Leistungsstarker PowerScan für anspruchsvolle Scan-Anforderungen.",
      },
      {
        imageSrc: "/hardware/DatalogicQuickScan.jpg",
        imageAlt: "Scanner Datalogic QuickScan",
        title: "Datalogic QuickScan",
        subtitle: "149,00 €",
        description:
          "Schneller QuickScan für reibungslosen Betrieb an der Kasse.",
      },
    ],
  },
  {
    label: "",
    items: [
      {
        imageSrc: "/hardware/AP-8220-U.jpg",
        imageAlt: "ARTDEV AP-8220-U",
        title: "ARTDEV AP-8220-U",
        subtitle: "249,00 €",
        description:
          "ARTDEV ist der nächste Schritt in einer Reihe von robusten, verlässlichen und leistungsstarken Kassendruckern.",
      },
      {
        imageSrc: "/hardware/BonUndEtiketten.jpg",
        imageAlt: "Citizen CT-S4500",
        title: "Citizen CT-S4500",
        subtitle: "249,00 €",
        description:
          "Vielseitiger Drucker für Bons und Etiketten – für jeden Einsatzbereich.",
      },
      {
        imageSrc: "/hardware/AP-8220-US.jpg",
        imageAlt: "ARTDEV AP-8220-US",
        title: "ARTDEV AP-8220-US",
        subtitle: "249,00 €",
        description:
          "ARTDEV ist der nächste Schritt in einer Reihe von robusten, verlässlichen und leistungsstarken Kassendruckern.",
      },
      {
        imageSrc: "/hardware/AP-8220-U.jpg",
        imageAlt: "ARTDEV AP-8300",
        title: "ARTDEV AP-8300",
        subtitle: "249,00 €",
        description:
          "ARTDEV ist der nächste Schritt in einer Reihe von robusten, verlässlichen und leistungsstarken Kassendruckern.",
      },
    ],
  },
];

/* =========================
   PAGE
========================= */

export default function PricingPage() {
  return (
    <ProductPage
      productName="Preise & Hardware – SDI Group"
      hero={{
        title: "Preise & Hardware",
        subtitle:
          "Transparente Preise und zuverlässige Hardware für Ihr Kassensystem – alles aus einer Hand für einen reibungslosen Geschäftsalltag.",
        buttonLeft: {
          title: "Unverbindlich anfragen",
          href: "/support",
        },
        buttonRight: {
          title: "Hardware ansehen",
          href: "#hardware",
        },
        image: ImageHero,
        imageAlt: "Preise & Hardware",
      }}
      highlight={{
        image: ImageHero,
        bullets: [
          <>
            <strong>Flexibles Leasing</strong> – ab 24,90 € / Monat, basierend
            auf 0,83 € pro Tag.
          </>,
          <>
            <strong>Mieten oder Ratenkauf</strong> – passend für jede
            Unternehmensgröße.
          </>,
          <>
            <strong>Hardware aus einer Hand</strong> – Scanner, Drucker,
            Displays und mehr.
          </>,
        ],
      }}
      beforeVideo={
        <>
          {/* PRICING CARDS */}
          <Section className="pb-24 bg-white border-y border-gray-50">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-7xl mx-auto px-4">
              <PricingCard
                        title="Leasing ab"
                        priceFull="24"
                        priceCents="90"
                        details="Basierend auf 0,83 € / Tag."
                        details2="36 / 48 / 60 Monate Laufzeit."
                        features={[
                          "Warenwirtschaft ModeProfi / CashTex",
                          "Voller Support & Updates & Schulungen",
                          "Inklusive Kassenbuch",
                          "Angebote, Order & Rechnungen",
                          "1 Benutzer",
                        ]}
                      />
              
                      <PricingCard
                        title="Mieten ab"
                        priceFull="48"
                        priceCents="90"
                        details="Basierend auf 1,63 € / Tag."
                        details2="36 / 48 / 60 Monate Laufzeit."
                        features={[
                          "Warenwirtschaft ModeProfi / CashTex",
                          "Voller Support & Updates & Schulungen",
                          "Inklusive Kassenbuch",
                          "Ideal für wachsende Unternehmen",
                          "1 Benutzer",
                        ]}
                      />
              
                      <PricingCard
                        title="Ratenkauf ab"
                        priceFull="74"
                        priceCents="90"
                        details="Basierend auf 2,49 € / Tag."
                        details2="36 / 48 / 60 Monate Laufzeit. Restwert 10 %"
                        features={[
                          "Warenwirtschaft ModeProfi / CashTex",
                          "Voller Support & Updates & Schulungen",
                          "Inklusive Kassenbuch",
                          "Eigentumserwerb nach Laufzeit",
                          "1 Benutzer",
                        ]}
                      />
            </div>

            <div className="text-center mt-12 text-sm font-bold text-gray-900 tracking-widest uppercase">
              Alle Preise exkl. MwSt.
            </div>
          </Section>

          {/* HARDWARE */}
          <Section id="hardware" className="bg-white py-24">
            {hardwareCategories.map((category, index) => (
              <div key={index} className="mb-20 px-6">
                {/* Nur anzeigen, wenn das Label nicht leer ist */}
                {category.label && (
                  <h3 className="text-sm font-bold text-gray-400 mb-8 border-b pb-2 uppercase tracking-widest">
                    {category.label}
                  </h3>
                )}

                <div className="flex flex-wrap justify-center gap-8">
                  {category.items.map((item) => (
                    <div
                      key={item.title}
                      className="w-full sm:w-[calc(50%-1rem)] md:w-[calc(33.333%-1.5rem)] xl:w-[calc(25%-1.5rem)]"
                    >
                      <HardwareCard
                        {...item}
                        linkUrl={buildSupportUrl(item.title, item.subtitle)}
                      />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </Section>
        </>
      }
      faqs={[
        {
          q: "Was ist der Unterschied zwischen Leasing, Mieten und Ratenkauf?",
          a: "Beim Leasing und Mieten zahlen Sie monatlich ohne Eigentumsübergang – ideal für niedrige Anfangsinvestitionen. Beim Ratenkauf erwerben Sie die Software nach Ablauf der 12 Monate als Eigentümer.",
        },
        {
          q: "Sind alle Preise inklusive MwSt.?",
          a: "Nein, alle angegebenen Preise verstehen sich exklusive der gesetzlichen Mehrwertsteuer. Der endgültige Betrag inklusive MwSt. wird Ihnen im Angebot ausgewiesen.",
        },
        {
          q: "Kann ich Hardware separat bestellen?",
          a: "Ja, unsere Hardware kann unabhängig von einem Software-Paket angefragt werden – nehmen Sie einfach Kontakt mit uns auf und wir beraten Sie gerne.",
        },
        {
          q: "Gibt es Mengenrabatte bei Hardware?",
          a: "Bei größeren Bestellmengen sprechen Sie uns gerne an – wir erstellen Ihnen ein individuelles Angebot, das auf Ihre Anforderungen zugeschnitten ist.",
        },
        {
          q: "Welche Zahlungsarten werden akzeptiert?",
          a: "Wir akzeptieren gängige Zahlungsarten wie Überweisung und auf Rechnung. Bei Fragen zur Zahlung stehen wir Ihnen gerne zur Verfügung.",
        },
        {
          q: "Kann ich ein Paket später erweitern oder upgraden?",
          a: "Ja, unsere Pakete sind flexibel – Sie können jederzeit auf ein höheres Paket wechseln oder zusätzliche Module hinzubuchen.",
        },
        {
          q: "Gibt es eine Mindestlaufzeit bei Miete oder Leasing?",
          a: "Die genauen Konditionen und Laufzeiten teilen wir Ihnen auf Anfrage mit – wir finden gemeinsam das passende Modell für Ihre Situation.",
        },
        {
          q: "Was ist im Support inbegriffen?",
          a: "Unser Support-Team ist von Montag bis Freitag von 09:30–13:00 Uhr und 14:00–18:00 Uhr per Telefon und E-Mail erreichbar – für alle Fragen rund um Software und Hardware.",
        },
      ]}
      features={[]}
    />
  );
}
